import { MaterialItem, OptimizationResult } from '@/types';

export interface SavedList {
  id: string;
  name: string;
  date: number;
  materials: MaterialItem[];
  result: OptimizationResult;
}

class IndexedDBService {
  private dbName = 'metalFabDB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  constructor() {
    this.initDB();
  }

  private initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains('savedLists')) {
          db.createObjectStore('savedLists', { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      request.onerror = (event) => {
        console.error('IndexedDB error:', (event.target as IDBOpenDBRequest).error);
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  async saveCutList(list: SavedList): Promise<string> {
    const db = await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['savedLists'], 'readwrite');
      const store = transaction.objectStore('savedLists');

      const request = store.put(list);

      request.onsuccess = () => {
        resolve(list.id);
      };

      request.onerror = (event) => {
        console.error('Error saving list:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  async getSavedLists(): Promise<SavedList[]> {
    const db = await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['savedLists'], 'readonly');
      const store = transaction.objectStore('savedLists');

      const request = store.getAll();

      request.onsuccess = (event) => {
        const lists = (event.target as IDBRequest<SavedList[]>).result;
        // Sort by date, newest first
        lists.sort((a, b) => b.date - a.date);
        resolve(lists);
      };

      request.onerror = (event) => {
        console.error('Error getting lists:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }

  async deleteSavedList(id: string): Promise<void> {
    const db = await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['savedLists'], 'readwrite');
      const store = transaction.objectStore('savedLists');

      const request = store.delete(id);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event) => {
        console.error('Error deleting list:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  }
}

export const indexedDBService = new IndexedDBService();
